import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DashboardLayout } from "@/components/Layout";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Upload as UploadIcon, Loader2, Search, ChevronRight, RefreshCw } from "lucide-react";

const TABS = [
  { id: "all", label: "All Reports" },
  { id: "pending", label: "Received" },
  { id: "processing", label: "AI Processing" },
  { id: "pending_review", label: "Awaiting Review" },
  { id: "published", label: "Published" },
  { id: "failed", label: "Failed" },
];

export default function LabReports() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [file, setFile] = useState(null);
  const [patientName, setPatientName] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => {
    setLoading(true);
    return api.get("/reports?lite=1").then((r) => setReports(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  // Auto-poll while any report is still processing/pending, so lab admins see
  // status flip to Published (or Failed) without hitting reload. Stops the poll
  // as soon as the queue is fully settled.
  useEffect(() => {
    const stillWorking = reports.some((r) => r.status === "processing" || r.status === "pending");
    if (!stillWorking) return;
    const t = setInterval(() => {
      api.get("/reports?lite=1").then((r) => setReports(r.data)).catch(() => {});
    }, 8000);
    return () => clearInterval(t);
  }, [reports]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file || !patientName) { toast.error("Choose file and patient name"); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("patient_name", patientName);
    if (user?.lab_id) fd.append("lab_id", user.lab_id);
    setBusy(true);
    try {
      await api.post("/reports/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Report queued for processing");
      setFile(null); setPatientName(""); load();
    } catch (err) { toast.error(apiError(err, "Upload failed")); }
    finally { setBusy(false); }
  };

  const isPublished = (r) => r.status === "published" || r.status === "ready";

  const counts = useMemo(() => {
    const c = { all: reports.length, pending: 0, processing: 0, pending_review: 0, published: 0, failed: 0 };
    for (const r of reports) {
      if (r.status === "pending") c.pending++;
      else if (r.status === "processing") c.processing++;
      else if (r.status === "pending_review") c.pending_review++;
      else if (r.status === "failed") c.failed++;
      else if (isPublished(r)) c.published++;
    }
    return c;
  }, [reports]);

  const filtered = useMemo(() => {
    let list = reports;
    if (tab === "pending") list = list.filter((r) => r.status === "pending");
    else if (tab === "processing") list = list.filter((r) => r.status === "processing");
    else if (tab === "pending_review") list = list.filter((r) => r.status === "pending_review");
    else if (tab === "published") list = list.filter((r) => isPublished(r));
    else if (tab === "failed") list = list.filter((r) => r.status === "failed");
    if (q) list = list.filter((r) => `${r.patient_name} ${r.package_name || ""}`.toLowerCase().includes(q.toLowerCase()));
    return list;
  }, [reports, tab, q]);

  return (
    <DashboardLayout role="lab_admin">
      <div>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-heading font-bold text-3xl text-ink">Reports</h1>
            <p className="text-ink/60">Track every report from arrival to patient delivery.</p>
          </div>
        </div>

        {/* Upload strip */}
        <Card className="mt-6 p-4 rounded-2xl">
          <form onSubmit={upload} className="grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
            <div><Label>Patient name</Label><Input data-testid="lab-upload-name" value={patientName} onChange={(e) => setPatientName(e.target.value)} placeholder="e.g. Anjali Sharma" /></div>
            <div><Label>Report file</Label><Input data-testid="lab-upload-file" type="file" accept=".pdf,image/*" onChange={(e) => setFile(e.target.files?.[0] || null)} /></div>
            <Button data-testid="lab-upload-submit" type="submit" disabled={busy} className="h-10 bg-ink hover:bg-ink/90 text-white">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UploadIcon className="w-4 h-4 mr-2" /> Upload Report</>}
            </Button>
          </form>
        </Card>

        {/* Tabs */}
        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="flex gap-1 flex-wrap" data-testid="lab-report-tabs">
            {TABS.map((t) => (
              <button key={t.id} data-testid={`lab-tab-${t.id}`} onClick={() => setTab(t.id)} className={`text-sm px-3 py-2 rounded-lg transition-all ${tab === t.id ? "bg-ink text-white font-medium" : "text-ink/70 hover:bg-muted"}`}>
                {t.label} <span className="ml-1 text-xs opacity-70">{counts[t.id] ?? 0}</span>
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ink/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search patient…" className="pl-9 h-10 w-64" />
          </div>
        </div>

        {/* Table */}
        <Card className="mt-4 rounded-2xl overflow-hidden">
          <div className="hidden lg:grid grid-cols-[1.5fr_1fr_0.8fr_0.8fr_1fr_0.7fr] items-center gap-3 px-5 py-3 text-[11px] uppercase tracking-wider text-ink/50 border-b border-border bg-muted/40">
            <div>Patient</div><div>Test Type</div><div>Received</div><div>Status</div><div>AI Findings</div><div className="text-right">Actions</div>
          </div>
          {loading && (
            <div className="p-6 space-y-3">
              {[0,1,2,3].map((i) => (
                <div key={i} className="h-14 rounded-xl bg-muted/40 animate-pulse" />
              ))}
            </div>
          )}
          {!loading && filtered.length === 0 && <div className="p-10 text-center text-ink/50">No reports here.</div>}
          {!loading && filtered.map((r) => <ReportRow key={r.id} r={r} onRerun={load} />)}
        </Card>
      </div>
    </DashboardLayout>
  );
}

function ReportRow({ r, onRerun }) {
  const [retrying, setRetrying] = useState(false);
  const initials = (r.patient_name || "R").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
  const need = r.smart_report?.parameters_needing_attention || 0;
  const isPublished = r.status === "published" || r.status === "ready";
  const label =
    r.status === "pending_review" ? "Awaiting Review" :
    isPublished ? "Published" :
    r.status === "processing" ? "AI Processing" :
    r.status === "pending" ? "Received" :
    r.status === "failed" ? "Failed" : r.status;
  const statusPillClass =
    isPublished ? "pill-normal" :
    r.status === "processing" ? "pill-borderline" :
    r.status === "pending_review" ? "pill-borderline" :
    r.status === "failed" ? "pill-high" :
    "pill-low";
  const finding = need > 0 ? `Critical: ${need} flagged` : isPublished ? "All normal" : "—";
  // Row link: pending_review reports go to the review page; others to the smart report viewer.
  const rowHref = r.status === "pending_review" || r.status === "failed" ? `/lab/reports/${r.id}/review` : `/report/${r.id}`;
  const doRetry = async (e) => {
    e.preventDefault(); e.stopPropagation();
    setRetrying(true);
    try {
      await api.post(`/reports/${r.id}/rerun`);
      toast.success("Re-running AI pipeline");
      onRerun && onRerun();
    } catch (err) { toast.error(apiError(err, "Retry failed")); }
    finally { setRetrying(false); }
  };
  return (
    <div className="border-b border-border hover:bg-muted/40 transition-all">
      {/* Desktop table row */}
      <div className="hidden lg:grid grid-cols-[1.5fr_1fr_0.8fr_0.8fr_1fr_0.7fr] items-center gap-3 px-5 py-3">
        <Link to={rowHref} data-testid={`lab-report-${r.id}`} className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-ink text-white grid place-items-center text-xs font-heading font-bold shrink-0">{initials}</div>
          <div className="min-w-0">
            <div className="font-medium text-ink truncate">{r.patient_name}</div>
            <div className="text-xs text-ink/50 truncate">{r.patient_age ? `${r.patient_age}y` : "—"}{r.patient_gender ? ` · ${r.patient_gender}` : ""}</div>
          </div>
        </Link>
        <div className="text-sm text-ink/80 truncate">{r.package_name || "Diagnostic Report"}</div>
        <div className="text-sm text-ink/60">{fmtAgo(r.created_at)}</div>
        <div><span className={`pill ${statusPillClass}`}>{label}</span></div>
        <div className="text-sm text-ink/70 truncate">{finding}</div>
        <div className="flex items-center justify-end gap-2">
          {r.status === "failed" && (
            <button onClick={doRetry} disabled={retrying} data-testid={`retry-${r.id}`} className="pill bg-ink text-white text-[11px] hover:opacity-90 disabled:opacity-50 flex items-center gap-1">
              {retrying ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Retry
            </button>
          )}
          {(r.status === "pending_review" || r.status === "failed") && (
            <Link to={`/lab/reports/${r.id}/review`} data-testid={`review-${r.id}`} className="pill bg-efgh-yellow text-ink text-[11px] hover:opacity-90">Review</Link>
          )}
          <Link to={rowHref} className="text-ink/40 hover:text-ink"><ChevronRight className="w-4 h-4" /></Link>
        </div>
      </div>
      {/* Mobile card row */}
      <Link to={rowHref} data-testid={`lab-report-m-${r.id}`} className="lg:hidden flex items-start gap-3 px-4 py-3">
        <div className="w-11 h-11 rounded-lg bg-ink text-white grid place-items-center text-sm font-heading font-bold shrink-0">{initials}</div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <div className="font-medium text-ink truncate">{r.patient_name}</div>
            <span className={`pill ${statusPillClass} shrink-0 text-[10px]`}>{label}</span>
          </div>
          <div className="text-xs text-ink/50 truncate mt-0.5">
            {r.patient_age ? `${r.patient_age}y` : "—"}{r.patient_gender ? ` · ${r.patient_gender}` : ""} · {r.package_name || "Diagnostic Report"}
          </div>
          <div className="mt-1.5 flex items-center justify-between gap-2 text-xs">
            <span className="text-ink/60">{fmtAgo(r.created_at)}</span>
            <span className="text-ink/70 truncate">{finding}</span>
          </div>
          {(r.status === "pending_review" || r.status === "failed") && (
            <div className="mt-2">
              <Link to={`/lab/reports/${r.id}/review`} data-testid={`review-m-${r.id}`} onClick={(e) => e.stopPropagation()} className="pill bg-efgh-yellow text-ink text-[11px]">Review now</Link>
            </div>
          )}
        </div>
        <ChevronRight className="w-4 h-4 text-ink/30 mt-1 shrink-0" />
      </Link>
    </div>
  );
}

function fmtAgo(iso) {
  if (!iso) return "";
  const dt = new Date(iso);
  const s = Math.max(1, Math.floor((Date.now() - dt.getTime()) / 1000));
  if (s < 60) return `${s} sec`;
  if (s < 3600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86400) return `${Math.floor(s / 3600)} hr ago`;
  return `${Math.floor(s / 86400)} d ago`;
}
